import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  Tab,
  Tabs
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileUpload from './FileUpload';
import { createLogger } from '../../utils/debugUtils';
import { authenticatedFetch } from '../../utils/apiUtils';

const logger = createLogger('ConfigurationForm');

/**
 * Configuration Form Component
 * Left panel for creating research jobs
 * 
 * @param {Object} props Component props
 * @param {Array} props.availableFilters List of available filters to select from
 * @param {Function} props.onCreateJob Callback when job is created
 * @param {boolean} props.isSubmitting Whether form is currently submitting
 * @param {Function} props.onSuccess Optional callback after successful job creation
 */
function ConfigurationForm({ availableFilters, onCreateJob, isSubmitting, onSuccess }) {
  // Source type state (file or filter)
  const [sourceType, setSourceType] = useState('filter'); // 'filter' or 'file'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [sourceFilterId, setSourceFilterId] = useState('');
  const [columnName, setColumnName] = useState('');
  const [jobName, setJobName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load columns when a filter is selected
  useEffect(() => {
    if (sourceFilterId && Array.isArray(availableFilters)) {
      const selectedFilter = availableFilters.find(f => f.id === sourceFilterId);
      if (selectedFilter && selectedFilter.columns) {
        setColumns(selectedFilter.columns);
        // Auto-select a column that likely contains URLs
        const urlColumn = selectedFilter.columns.find(col => 
          col.name.toLowerCase().includes('url') || 
          col.name.toLowerCase().includes('website')
        );
        if (urlColumn) {
          setColumnName(urlColumn.name);
        } else if (selectedFilter.columns.length > 0) {
          setColumnName(selectedFilter.columns[0].name);
        }
      }
    } else {
      setColumns([]);
      setColumnName('');
    }
  }, [sourceFilterId, availableFilters]);

  // Handle file upload
  const handleFileProcessed = async (file, formData) => {
    if (!file) return;
    
    setError('');
    setIsUploading(true);
    
    try {
      // Log file details for debugging
      logger.info('Starting file upload process:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        formDataContainsFile: formData.has('file')
      });
      
      // Make API call to upload file with authentication
      const response = await authenticatedFetch('/api/research/upload', {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header for FormData, browser will set it with boundary
        headers: {}
      });
      
      // Log response status for debugging
      logger.info(`Upload response status: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        // Try to get error details from response
        let errorDetails = '';
        try {
          const errorData = await response.json();
          errorDetails = errorData.error || errorData.message || '';
          logger.error('Server error details:', errorData);
        } catch (e) {
          // If we can't parse the JSON, just use the status text
          errorDetails = response.statusText;
        }
        
        throw new Error(`File upload failed: ${errorDetails}`);
      }
      
      const data = await response.json();
      logger.info('Upload response data:', data);
      
      if (data && data.fileId && data.columns) {
        setUploadedFile({
          id: data.fileId,
          name: file.name,
          path: data.filePath // Store the file path if available
        });
        
        setAvailableColumns(data.columns);
        
        // Pre-select URL column if available
        const urlColumn = data.columns.find(col => 
          col.toLowerCase().includes('url') || col.toLowerCase().includes('website')
        );
        
        if (urlColumn) {
          setSelectedColumn(urlColumn);
        } else if (data.columns.length > 0) {
          setSelectedColumn(data.columns[0]);
        }
        
        logger.info('File uploaded successfully with ID:', data.fileId);
      } else {
        // Handle unexpected response format
        logger.error('Invalid response structure:', data);
        throw new Error('Server returned an invalid response format');
      }
    } catch (error) {
      logger.error('Error uploading file:', error);
      setError('Failed to upload file: ' + (error.message || 'Unknown error'));
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validate instructions
    if (!instructions.trim()) {
      setError('Please provide research instructions');
      return;
    }
    
    // Validate data source based on source type
    if (sourceType === 'filter') {
      if (!sourceFilterId) {
        setError('Please select a source filter');
        return;
      }

      if (!columnName) {
        setError('Please select a column containing website URLs');
        return;
      }
    } else if (sourceType === 'file') {
      if (!uploadedFile) {
        setError('Please upload a CSV file');
        return;
      }
      
      if (!selectedColumn) {
        setError('Please select a column containing website URLs');
        return;
      }
    }
    
    // Log job creation details
    if (sourceType === 'filter') {
      logger.info('Creating job with sourceFilterId:', sourceFilterId, 'columnName:', columnName);
    } else {
      logger.info('Creating job with uploadedFile:', uploadedFile.id, 'selectedColumn:', selectedColumn);
    }
    
    // Create job configuration
    const jobConfig = {
      sourceType,
      columnName: sourceType === 'filter' ? columnName : selectedColumn,
      name: jobName.trim(), // Include job name in configuration
      instructions: instructions.trim(),
      configuration: {
        // Additional configuration options can go here
      }
    };
    
    // Add source-specific properties based on the source type
    if (sourceType === 'filter') {
      jobConfig.sourceFilterId = sourceFilterId;
      jobConfig.columnName = columnName;
    } else if (sourceType === 'file') {
      jobConfig.uploadedFileId = uploadedFile.id;
      jobConfig.columnName = selectedColumn;
      jobConfig.filePath = uploadedFile.path; // Include file path for export feature
      
      // Log file path being sent for debugging
      logger.info('Including file path in job creation:', uploadedFile.path);
    }

    // Call parent handler
    onCreateJob(jobConfig);
    
    // Show success message
    setSuccessMessage('Research job created successfully!');
    
    // Reset form fields
    setJobName('');
    setInstructions('');
    
    // Reset source-specific fields
    if (sourceType === 'file') {
      setUploadedFile(null);
      setSelectedColumn('');
      setAvailableColumns([]);
    }
    
    // Call onSuccess callback if provided
    if (onSuccess && typeof onSuccess === 'function') {
      setTimeout(() => {
        onSuccess();
      }, 1000); // Small delay to ensure user sees the success message before redirect
    }
  };

  // Handle tab change
  const handleSourceTypeChange = (event, newValue) => {
    setSourceType(newValue);
    setError(''); // Clear any errors when switching tabs
  };
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Research Configuration
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}
      
      {/* Source Type Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={sourceType} 
          onChange={handleSourceTypeChange}
          aria-label="data source tabs"
        >
          <Tab label="From Filter" value="filter" />
          <Tab label="Upload CSV" value="file" />
        </Tabs>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* Job Name Field - Common for both sources */}
        <FormControl fullWidth margin="normal">
          <TextField
            id="job-name"
            label="Research Job Name"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            placeholder="Enter a recognizable name for this job"
            helperText="Give your research job a descriptive name to find it easily later"
          />
        </FormControl>

        {/* Filter Source */}
        {sourceType === 'filter' && (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel id="filter-select-label">Source Filter</InputLabel>
              <Select
                labelId="filter-select-label"
                id="filter-select"
                value={sourceFilterId}
                label="Source Filter"
                onChange={(e) => setSourceFilterId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select a filter</em>
                </MenuItem>
                {Array.isArray(availableFilters) && availableFilters.length > 0 ? (
                  availableFilters.map((filter) => (
                    <MenuItem key={filter.id} value={filter.id}>
                      {filter.name || filter.id} ({filter.keptCount || 0} websites)
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <em>No filters available</em>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel id="column-select-label">Website Column</InputLabel>
              <Select
                labelId="column-select-label"
                id="column-select"
                value={columnName}
                label="Website Column"
                onChange={(e) => setColumnName(e.target.value)}
                disabled={!sourceFilterId || columns.length === 0}
              >
                <MenuItem value="">
                  <em>Select a column</em>
                </MenuItem>
                {Array.isArray(columns) ? columns.map((column) => (
                  <MenuItem key={column.name} value={column.name}>
                    {column.name}
                  </MenuItem>
                )) : null}
              </Select>
            </FormControl>
          </>
        )}
        
        {/* File Upload Source */}
        {sourceType === 'file' && (
          <FileUpload 
            onFileProcessed={handleFileProcessed}
            isUploading={isUploading}
            availableColumns={availableColumns}
            selectedColumn={selectedColumn}
            onColumnChange={setSelectedColumn}
            jobName={jobName}
            onJobNameChange={setJobName}
          />
        )}

        <FormControl fullWidth margin="normal">
          <TextField
            id="instructions"
            label="Research Instructions"
            multiline
            rows={6}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Describe what makes a website a good fit for your needs..."
            helperText="Be specific about what criteria make a website valuable to you"
          />
        </FormControl>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SearchIcon />}
            disabled={
              (sourceType === 'filter' && (!sourceFilterId || !columnName)) ||
              (sourceType === 'file' && (!uploadedFile || !selectedColumn)) ||
              !instructions.trim() ||
              isSubmitting
            }
          >
            {isSubmitting ? 'Starting...' : 'Start Research'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default ConfigurationForm;
